/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent, TSimpleEventEmitter } from "@adapptio/lib-shared";
import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import { Dialog, IDialogConstructor, TGetDialogCloseResult, TGetDialogOpenParams } from "./Dialog";
import { Breadcrumb } from "../decorators";

/**
 * Dialog entry states
 */
export enum DIALOG_ENTRY_STATE {
	INIT = "init",
	OPENED = "open",
	CLOSED = "closed"
}

/**
 * Dialog entry
 */
export interface IDialogEntry {
	readonly id: string;
	readonly instance: Dialog;
	readonly onStateChange: TSimpleEventEmitter<void>;
	state: DIALOG_ENTRY_STATE;
}

interface IDialogInstanceIndex {
	[K: string]: IDialogEntry;
}

const DIALOG_OPEN_DELAY = 10;
const DIALOG_CLOSE_DELAY = 600;

/**
 * Service options
 */
export interface IDialogServiceOpts {}

/**
 * Service to manage commands
 */
export class DialogService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** Opened dialogs instances */
	private dialogs: IDialogEntry[] = [];

	/** ID index */
	private idIndex: IDialogInstanceIndex = {};

	/** Emitted when entries list has changed */
	public readonly onEntriesChange = createEventEmitter<void>();

	/** Emitted when a dialog was opened */
	public readonly onDialogOpen = createEventEmitter<Dialog>();

	/** Emitted when a dialog has been closed */
	public readonly onDialogClose = createEventEmitter<Dialog>();

	/**
	 * Service Constructor
	 *
	 * @param dc Dependency Container
	 * @param opts Configuration options
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public constructor(private dc: IEditor, opts: IDialogServiceOpts) {
		this.logger = dc.get("logger").facility("DialogService");
	}

	/**
	 * Opens dialog and returns promise that is resolved when the dialog is closed
	 *
	 * @param ctor Dialog constructor
	 * @param id Unique dialog ID
	 * @param params Parameters
	 */
	@Breadcrumb("dialog.open", (ctor, id) => String(id), (a, b, params) => ({ params }))
	public open<TDialog extends Dialog>(
		ctor: IDialogConstructor<TDialog>,
		id: string,
		params: TGetDialogOpenParams<TDialog>
	): Promise<TGetDialogCloseResult<TDialog>> {
		// Instance with ID that already exists
		if (this.idIndex[id]) {
			if (this.idIndex[id].instance instanceof ctor) {
				this.logger.debug(`Dialog '${ctor.name}' with ID '${id}' already exists.`);
				return this.idIndex[id].instance.getClosePromise() as Promise<TGetDialogCloseResult<TDialog>>;
			} else {
				// eslint-disable-next-line max-len
				this.logger.warn(
					`Trying to open dialog '${ctor.name}' with ID '${id}' but instance with the same ID and different constructor exists.`
				);
				throw new Error(`Dialog with ID '${id}' is already opened and has different constructor.`);
			}
		}

		this.logger.debug(`Opening dialog '${ctor.name}' with ID '${id}' and params:`, params);

		// Create new dialog
		let entry: IDialogEntry;

		try {
			entry = {
				id: id,
				instance: new ctor(this.dc, id, params),
				state: DIALOG_ENTRY_STATE.INIT,
				onStateChange: createEventEmitter()
			};
		} catch (err) {
			this.logger.warn(`Failed to open dialog '${ctor.name}' with ID '${id}' and params:`, params, err);
			throw err;
		}

		this.dialogs.push(entry);
		this.idIndex[id] = entry;

		const openTimer = setTimeout(() => {
			this.logger.debug(`Dialog ID '${id}' state changed to OPENED.`);

			entry.state = DIALOG_ENTRY_STATE.OPENED;
			emitEvent(entry.onStateChange);

			try {
				emitEvent(this.onDialogOpen, entry.instance);
			} catch (err) {
				this.logger.warn(`Failed to handle onDialogOpen event for dialog with ID '${id}':`, err);
			}
		}, DIALOG_OPEN_DELAY);

		entry.instance.getClosePromise().then(() => {
			clearTimeout(openTimer);

			this.logger.debug(`Dialog ID '${id}' state changed to CLOSED.`);

			entry.state = DIALOG_ENTRY_STATE.CLOSED;
			emitEvent(entry.onStateChange);

			try {
				emitEvent(this.onDialogClose, entry.instance);
			} catch (err) {
				this.logger.warn(`Failed to handle onDialogClose event for dialog with ID '${id}':`, err);
			}

			setTimeout(() => {
				this.destroyDialog(entry);
			}, DIALOG_CLOSE_DELAY);
		});

		emitEvent(this.onEntriesChange);

		return entry.instance.getClosePromise() as Promise<TGetDialogCloseResult<TDialog>>;
	}

	/**
	 * Returns current dialog entries
	 */
	public getEntries(): IDialogEntry[] {
		return this.dialogs;
	}

	/**
	 * Returns dialog instance by ID or null if not exists
	 *
	 * @param id Dialog ID
	 */
	public getDialogById<TDialog extends Dialog>(id: string): TDialog {
		return (this.idIndex[id].instance as TDialog) || null;
	}

	/**
	 * Returns if a dialog with given ID exists
	 *
	 * @param id Dialog ID
	 */
	public isOpened(id: string): boolean {
		return this.idIndex[id] ? true : false;
	}

	/**
	 * Disposes dialog instance and removes it from list and index
	 *
	 * @param entry Dialog entry
	 */
	@Breadcrumb("dialog.destroy", (entry: IDialogEntry) => entry?.id)
	private destroyDialog(entry: IDialogEntry) {
		const i = this.dialogs.indexOf(entry);

		if (i >= 0) {
			this.dialogs.splice(i, 1);
		}

		delete this.idIndex[entry.id];

		try {
			entry.instance.dispose();
			this.logger.debug(`Dialog ID '${entry.id}' has been removed.`);
		} catch (err) {
			this.logger.warn(`Failed to dispose dialog ID '${entry.id}':`, err);
		}

		emitEvent(this.onEntriesChange);
	}
}
